import React from 'react';
import styled from 'styled-components';
import SearchComponent from './SearchComponent';
import colors from '../../theme/colors';
import { IconSearch, IconOverview, IconTarget } from '../icons';

const CameraControlsWrapper = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 1000;
  background: none;
  border: 1px solid ${colors.base.themeSecondaryTransparent};
  border-radius: 10px;
  padding: 10px;
  transition: all 0.3s ease;

  /* Bring controls + handle above other overlays when last clicked */
  &:focus-within {
    z-index: 1200;
  }

  /* Mobile: collapsible from left, anchored to screen bottom */
  @media (max-width: 768px), (max-height: 768px) {
    left: 0;
    bottom: 10px;
    top: auto;
    transform: translateX(${props => props.$collapsed ? 'calc(-100% + 10px)' : '0'});
    border-radius: ${props => props.$collapsed ? '0 10px 10px 0' : '10px'};
  }
`;

const ControlsToggle = styled.button`
  display: none; /* Hidden on desktop */

  /* Mobile: show toggle button (works in landscape too) */
  @media (max-width: 768px), (max-height: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: -30px;
    left: auto;
    top: 50%;
    transform: translateY(-50%);
    width: 26px;
    height: 26px;
    background: ${colors.primary.darkRgba};
    border: 1px solid ${colors.button.aboutBorder}; /* light contour like Read Me */
    border-radius: 50%;
    color: white;
    cursor: pointer;
    padding: 0;
    transition: all 0.2s ease;
    box-shadow:
      0 10px 24px rgba(0, 0, 0, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.12);
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    user-select: none;

    svg {
      width: 14px;
      height: 14px;
      stroke: rgba(255, 255, 255, 0.95);
      stroke-width: 2.6;
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
    }

    &:hover {
      background: ${colors.button.aboutBgHover};
      border-color: ${colors.border.lightHover};
      transform: translateY(-50%) scale(1.15);
      box-shadow: ${colors.effects.shadowLarge};
    }

    &:active {
      transform: translateY(-50%) scale(0.95);
    }
  }
`;

const ControlButton = styled.button`
  background: ${colors.base.themePrimary};
  border: 1px solid ${colors.base.themeSecondaryTransparent};
  border-radius: 20px;
  color: white;
  padding: 12px 18px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  cursor: pointer;
  transition: all 0.2s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: ${colors.button.aboutBgHover};
    border-color: ${colors.border.lightHover};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CameraControls = ({
  collapsed,
  onToggle,
  onToggleSearch,
  onResetCamera,
  onFocusOnSelected,
  selectedNodeId,
  translations,
  // Search props
  showSearch,
  searchQuery,
  searchResults,
  onSearchChange,
  onSelectResult
}) => {
  return (
    <CameraControlsWrapper $collapsed={collapsed}>
      <ControlsToggle
        onClick={onToggle}
        title={collapsed ? "Show controls" : "Hide controls"}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ transform: `rotate(${collapsed ? -90 : 90}deg)` }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </ControlsToggle>

      {showSearch ? (
        <SearchComponent
          searchQuery={searchQuery}
          searchResults={searchResults}
          onSearchChange={onSearchChange}
          onSelectResult={onSelectResult}
          onClose={onToggleSearch}
          translations={translations}
        />
      ) : (
        <ControlButton onClick={onToggleSearch}>
          <IconSearch size={15} /> {translations?.searchCtrlF || "Search (Ctrl+F)"}
        </ControlButton>
      )}

      <ControlButton onClick={onResetCamera}>
        <IconOverview size={15} /> {translations?.overview || "Overview"}
      </ControlButton>

      <ControlButton
        onClick={onFocusOnSelected}
        disabled={!selectedNodeId}
      >
        <IconTarget size={15} /> {translations?.focusOnSelected || "Focus on Selected"}
      </ControlButton>
    </CameraControlsWrapper>
  );
};

export default CameraControls;
